import type { Group, PhotoStyle } from '../lib/types';

/**
 * The group's photo as a small circle, or the leader's initials when there is
 * no photo — which is every group until staff upload one in CCB.
 *
 * Deliberately sized and placed to sit *beside* the title rather than above it:
 * the brief was that photos should not become the thing the eye lands on. At
 * 44px it reads as a profile picture next to a name, and the card's information
 * hierarchy — name, then when and where, then Sign Up — is unchanged. Because
 * the monogram fills the same circle, a list where only some groups have photos
 * still lines up instead of going ragged.
 */
/**
 * The leader's face, or nothing at all.
 *
 * `photoFace`, never `photo`: the full photo is a landscape banner, and in a
 * round frame it shows the middle of the artwork rather than a face.
 * `photoFace` is a square crop cut around the detected face during the refresh.
 *
 * Returns null when there is no crop. An initials monogram was tried here and
 * dropped — a circle is an attention-getter, and spending one on two letters
 * draws the eye to the groups with the LEAST to show. A card with no photo now
 * simply prints the leader's name, which is the information anyway.
 */
export function GroupAvatar({ group, size = 'sm' }: { group: Group; size?: 'sm' | 'lg' }) {
  /*
   * The Crossroads mark, for a listing credited to a role rather than a person
   * ("Led by Pastoral Staff"). A face crop there shows one particular leader
   * and reads as a contradiction of the byline beside it. This is the one case
   * where a circle without a photo earns its place: it is the church's own
   * mark, not two letters standing in for a picture.
   */
  if (group.logoAvatar) {
    return (
      <div className={`avatar avatar--${size} avatar--logo`} aria-hidden="true">
        <img
          className="avatar__logo"
          src="/brand/crossroads-icon-white.svg"
          alt=""
          decoding="async"
        />
      </div>
    );
  }

  if (!group.photoFace) return null;

  return (
    // Decorative: the leader's name is printed immediately beside it, so a
    // screen reader announcing the circle as well would just say it twice.
    <div className={`avatar avatar--${size}`} aria-hidden="true">
      {/*
        Deliberately NOT loading="lazy". These are 30px thumbnails of about
        15 KB and there are only ever a handful, so deferring them saves
        nothing — while making them dependent on the browser's near-viewport
        heuristic, which left every portrait unloaded when the byline sat below
        the fold. Eager is both simpler and what actually renders.
      */}
      <img className="avatar__img" src={group.photoFace} alt="" width={30} height={30} decoding="async" />
    </div>
  );
}

/** Full name. Contact details are never in the data to begin with. */
export function leaderNameOf(group: Group): string {
  return [group.leaderFirstName, group.leaderLastName].filter(Boolean).join(' ');
}

export function SignUpButton({ group, block }: { group: Group; block?: boolean }) {
  return (
    <a
      className={`btn btn--primary${block ? ' btn--block' : ''}`}
      href={group.signupUrl}
      target="_blank"
      rel="noopener noreferrer"
      // Sits above the card's full-bleed details trigger so a tap on the button
      // goes to CCB, not the detail panel.
      onClick={(event) => event.stopPropagation()}
    >
      Sign Up
      <span className="visually-hidden"> for {group.name} (opens in a new tab)</span>
    </a>
  );
}

export function MetaLine({ group }: { group: Group }) {
  // `location`, not `campus`: a group meeting in a home in Ashland is filed
  // under Mansfield Campus in CCB, and showing that here reads as the wrong place.
  const parts = [group.location, [group.meetingDay, group.meetingTime].filter(Boolean).join(' · ')];
  return <p className="card__meta">{parts.filter(Boolean).join(' — ')}</p>;
}

/**
 * `showTerm` is on only when the page is listing more than one term at once.
 * Several groups repeat verbatim across terms — same name, leader, and campus —
 * so without it two cards look identical. In single-term mode the tag would be
 * noise on every card, so it stays off.
 */
export function Tags({ group, showTerm }: { group: Group; showTerm?: boolean }) {
  const spots = group.spotsLeft;
  const members = group.currentMembers;

  return (
    <ul className="tags">
      {showTerm && group.termLabel && <li className="tag tag--term">{group.termLabel}</li>}
      {group.audience && <li className="tag">{group.audience}</li>}
      {group.kidsMinistry && <li className="tag">Kids Ministry</li>}
      {/*
        How many people are in the group now — the same number CCB's own public
        list shows. It reads as "is anyone actually in this?", which is the
        question a new person is really asking, and it pairs with spots left.
        Quieter styling than the audience tags: it is context, not a category.
      */}
      {members !== null && members > 0 && (
        <li className="tag tag--count">
          {members} {members === 1 ? 'member' : 'members'}
        </li>
      )}
      {/* Only shown when capacity is a real number — "Unlimited" gets nothing. */}
      {group.isFull && <li className="tag tag--full">Group full</li>}
      {!group.isFull && spots !== null && spots <= 5 && (
        <li className="tag tag--spots">
          {spots} {spots === 1 ? 'spot' : 'spots'} left
        </li>
      )}
    </ul>
  );
}

interface GroupCardProps {
  group: Group;
  onOpen: (group: Group) => void;
  showTerm?: boolean;
  photoStyle?: PhotoStyle;
}

export function GroupCard({ group, onOpen, showTerm, photoStyle = 'banner' }: GroupCardProps) {
  const leaderName = leaderNameOf(group);

  /*
    Two ways to show the photo, because the right one depends on what staff
    upload:

    banner — the photo runs across the top of the card at its native 16:9.
      This suits what CCB actually holds today: Crossroads-branded artwork with
      the group name set in type and the leader cut out on the right. Cropping
      that to a circle throws away the design and lands on a sliver of nothing.

    avatar — a small circle beside the title, the leader's initials when there
      is no photo. This is the right call if staff move to square headshots,
      and it keeps photos from leading the card.

    Set via `site.photoStyle` in config.json — no code change to switch.
  */
  const showBanner = photoStyle === 'banner';
  const showAvatar = photoStyle === 'avatar';

  return (
    <article className="card">
      {/*
        In banner mode every card gets the same 16:9 block, photo or not —
        otherwise a group without a photo sits with its title at the top of the
        row while its neighbours' titles start 130px lower, and the grid reads
        as broken rather than as varied. The placeholder is a tinted block with
        the group's initials, which is quiet enough not to look like a failed
        image load.
      */}
      {showBanner &&
        (group.photo ? (
          <div className="card__media">
            <img src={group.photo} alt="" loading="lazy" decoding="async" />
          </div>
        ) : (
          <div className="card__media card__media--empty" aria-hidden="true">
            <span className="card__media-monogram">{group.leaderInitials ?? '·'}</span>
          </div>
        ))}

      <div className="card__body">
        <h3 className="card__title">{group.name}</h3>
        <MetaLine group={group} />
        <Tags group={group} showTerm={showTerm} />
        {group.excerpt && <p className="card__excerpt">{group.excerpt}</p>}

        {/*
          The leader sits at the FOOT of the card, not beside the title. Up
          there the photo was the first thing the eye landed on, ahead of the
          group's name and when it meets — which is backwards for a page whose
          job is helping someone find a group. Down here it reads as a byline:
          present, but after the information that decides anything.
        */}
        {/* A div, not a p: GroupAvatar renders a block element, and a div
            inside a paragraph is invalid and gets reparented by the browser. */}
        {leaderName && (
          <div className="card__leader">
            {showAvatar && <GroupAvatar group={group} />}
            <span>Led by {leaderName}</span>
          </div>
        )}
      </div>

      <div className="card__actions">
        <SignUpButton group={group} />
        <span className="card__details-hint" aria-hidden="true">
          Details
        </span>
      </div>

      {/*
        Full-card trigger: the whole card is one big tap target on a phone, and
        it is still a real focusable button for keyboard and screen readers.
      */}
      <button
        type="button"
        className="card__trigger"
        aria-label={`View details for ${group.name}`}
        onClick={() => onOpen(group)}
      />
    </article>
  );
}
